import { Box, Button, Card, CardBody, SimpleGrid, VStack } from '@chakra-ui/react';
import { usePropertiesModel } from '@common/models/PropertiesModel';
import { createDateFromShortISO, formatDate } from '@common/utility/datetime';
import { FontFamily } from '@common/theming/Fonts';
import React, { useState } from 'react';
import { TextBlock } from 'src/apps/invest/common/components/TextBlock';
import { SectionHeader } from '../common/SectionHeader';

type RecentUpdatesProps = {
  children?: React.ReactNode;
};

export const RecentUpdates: React.FC<RecentUpdatesProps> = () => {
  const { property } = usePropertiesModel();
  const [isExpanded, setIsExpanded] = useState(false);

  const updates = property?.sfr?.status.updates ?? [];
  const visibleUpdates = isExpanded ? updates : updates.slice(0, 4);
  const hasMoreUpdates = updates.length > 4;

  return (
    <VStack alignItems={'flex-start'} spacing={0} width="100%">
      <Box mb={['0px', '40px']}>
        <SectionHeader text="Recent Updates" />
      </Box>

      <Box position="relative" width="100%" paddingBottom={isExpanded ? '60px' : '0px'}>
        <SimpleGrid columns={[1, 2]} gap="30px" width="100%" mb={['20px', '40px']}>
          {visibleUpdates.map((update, i) => (
            <Card key={`update-${i}`} border={`1px solid #D5D6D8`} boxShadow={'none'}>
              <CardBody>
                <TextBlock fontSize="16px" color="gray.700">
                  {formatDate(createDateFromShortISO(update.date), 'MMM D, YYYY')}
                </TextBlock>
                <TextBlock fontSize="20px" weight="bold" mb="10px">
                  {update.title}
                </TextBlock>
                <TextBlock fontSize="20px" lineHeight={'24px'}>
                  {update.description}
                </TextBlock>
              </CardBody>
            </Card>
          ))}
        </SimpleGrid>

        {/* Gradient Overlay */}
        {!isExpanded && hasMoreUpdates && (
          <Box
            position="absolute"
            bottom="0"
            left="0"
            right="0"
            height="200px"
            pointerEvents="none"
            background="linear-gradient(to bottom, rgba(255,255,255,0) 0%, rgba(255,255,255,0.95) 40%, rgba(255,255,255,1) 70%)"
          />
        )}

        {/* Button Container - Now absolutely positioned */}
        {hasMoreUpdates && (
          <Box
            position="absolute"
            bottom={isExpanded ? '0px' : '40px'}
            left="0"
            right="0"
            display="flex"
            justifyContent="center"
            zIndex={2}
            transition="bottom 0.2s ease-in-out">
            <Button
              onClick={() => setIsExpanded(!isExpanded)}
              fontFamily={FontFamily.HELVETICA_NOW_BOLD}
              fontSize="16px"
              borderRadius="25px"
              boxShadow="0px 2px 4px rgba(0, 0, 0, 0.1)"
              bg="white"
              color="black"
              border="1px solid"
              borderColor="gray.200"
              _hover={{
                bg: 'gray.50',
                boxShadow: '0px 4px 8px rgba(0, 0, 0, 0.1)',
                transform: 'translateY(-1px)',
              }}
              _active={{
                bg: 'gray.100',
                transform: 'translateY(0)',
              }}
              height="50px"
              px="40px">
              {isExpanded ? 'Show Less' : 'Show More Updates'}
            </Button>
          </Box>
        )}
      </Box>
    </VStack>
  );
};
